import React, { useRef, useState } from 'react';
import {
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails,
  AccordionContainer,
  StyledComment,
} from 'components/molecules/ThreadAccordion/styles';
import { CommentForm } from 'pages/CaseManagementPage/CasesPage/CaseDetailsReviewCommentPage/CommentsAccordions/CommentForm';
import { Grid, Typography } from '@mui/material';
import { colors } from 'theme/colors';
import Icon from 'components/atoms/Icon/Icon';
import { EIconTypes } from 'constants/Icons';
import { ICaseThread, ICommentForThreadFormValues, IThread } from 'models/cases';
import { useTranslations } from 'hooks/useTranslations';
import { useScrollToLatestItem } from 'hooks/useScrollToLatestItem';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { formatDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { getHtmlText } from 'components/organisms/HtmlSlate';

export interface ICaseThreadsAccordions {
  items: ICaseThread[];
  maxVisibleItems?: number;
  defaultExpandedIndex?: number | false;
  onReload: () => void;
  onSubmitReply: (
    threadId: string,
    body: ICommentForThreadFormValues,
  ) => Promise<boolean | void>;
  isReadOnly?: boolean;
  isAssignUser: boolean;
  isSupervisor: boolean;
}

const formatLastCommentLine = (
  item: IThread,
  t: (key: string) => string,
  dateFormat: string,
): string => {
  const lastComment = item.comments[item.comments.length - 1];
  if (!lastComment) {
    return '';
  }
  return `${t('general.last.label')}: ${formatDate(lastComment.createdDate, dateFormat)} ${t('general.by.label')} ${lastComment.firstName} ${lastComment.lastName} (${lastComment.username})`;
};

export const CaseThreadsAccordions: React.FC<ICaseThreadsAccordions> = ({
  items,
  maxVisibleItems,
  defaultExpandedIndex = false,
  onReload,
  onSubmitReply,
  isReadOnly = false,
  isAssignUser,
  isSupervisor,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslations();
  const [expanded, setExpanded] = useState<number | false>(defaultExpandedIndex);
  const { shortDateFormat, longDateFormat } = useGlobalProperty();
  const [replyThreadId, setReplyThreadId] = useState<string | null>(null);

  useScrollToLatestItem(items, containerRef, setExpanded);

  const closeCommentForm = () => {
    setReplyThreadId(null);
  };

  const submitReplyForm = async (data: ICommentForThreadFormValues) => {
    if (replyThreadId) {
      await onSubmitReply(replyThreadId, data);
      closeCommentForm();
      onReload();
    }
  };

  const handleChange =
    (index: number) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? index : false);
      setReplyThreadId(null);
    };

  if (!items.length) {
    return null;
  }

  return (
    <AccordionContainer maxVisibleItems={maxVisibleItems} ref={containerRef}>
      {items.map((item, index) => (
        <StyledAccordion
          key={item.uniqueId}
          expanded={expanded === index}
          onChange={handleChange(index)}
        >
          <StyledAccordionSummary
            expandIcon={
              <>
                <span className="icon-plus">
                  <Icon type={EIconTypes.plus} fontSize={11} color={colors.black} />
                </span>

                <span className="icon-minus">
                  <Icon type={EIconTypes.minus} fontSize={11} color={colors.black} />
                </span>
              </>
            }
            aria-controls={`panel${index}-content`}
            id={`panel${index}-header`}
          >
            <Grid>
              <Typography variant="subtitle2" sx={{ color: colors.primary900 }}>
                {item.topic}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.primary900 }}>
                {formatLastCommentLine(item, t, longDateFormat)}
              </Typography>
            </Grid>
          </StyledAccordionSummary>
          <StyledAccordionDetails>
            {item.comments.map((comment, commentIndex) => (
              <StyledComment first={commentIndex === 0} key={comment.uniqueId}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2">
                    {`${formatDate(comment.createdDate, shortDateFormat)} ${comment.firstName} ${comment.lastName} (${comment.username})`}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2">{getHtmlText(comment.comment)}</Typography>
                </Grid>
              </StyledComment>
            ))}
            <Grid sx={{ textAlign: 'right' }}>
              {(!replyThreadId || replyThreadId !== item.uniqueId) &&
                (isAssignUser || isSupervisor) &&
                !isReadOnly && (
                  <Button
                    label={t('caseManagement.cases.replyInThread.label')}
                    variant={EButtonVariants.outlined}
                    size={EButtonSizes.small}
                    onClick={() => setReplyThreadId(item.uniqueId)}
                    id="case_threads_accordions_reply_in_thread-button"
                  />
                )}
            </Grid>
          </StyledAccordionDetails>
          <Grid sx={{ p: 1 }}>
            {!isReadOnly &&
              (isAssignUser || isSupervisor) &&
              replyThreadId === item.uniqueId && (
                <CommentForm
                  submitForm={submitReplyForm}
                  closeCommentForm={closeCommentForm}
                />
              )}
          </Grid>
        </StyledAccordion>
      ))}
    </AccordionContainer>
  );
};
